<script setup lang="ts">
import {inject, ref} from "vue";
import {useRouter} from "vue-router";
import {FilterMatchMode} from "@primevue/core/api";
import {getPlans as apiGetPlans} from "@/Services/Admin/admin-plans";
import {Button, Column, DataTable, MultiSelect} from "@/Components/Library";
import {PageHeading, TableRecordActions} from "@/Components/Global";
import type dayjs from "dayjs";
import type {AdminIndex} from "@/Types";

type PlanLocal = any;

const $date = inject("date") as typeof dayjs;
const router = useRouter();

const plans = ref<AdminIndex<PlanLocal>>();

const filters = ref({
    is_active: {
        value: null,
        matchMode: FilterMatchMode.EQUALS,
    },
});

const getPlans = async () => apiGetPlans(router.currentRoute.value.query).then((data) => (plans.value = data));
getPlans();
</script>
<template>
    <div>
        <div class="m-container">
            <PageHeading title="Plans">
                <RouterLink class="ml-auto focus-visible:outline-none" :to="{name: 'admin.plans.create'}">
                    <Button
                        rounded
                        class="bg-askr-green text-white transition duration-200 hover:brightness-110"
                        icon="prime:plus"
                    >
                        Add Plan
                    </Button>
                </RouterLink>
            </PageHeading>
        </div>
        <DataTable
            :data="plans"
            dataKey="id"
            :loading="false"
            :filters="filters"
            @filter="getPlans"
            :allowTagSearch="false"
            defaultSort="-created_at"
        >
            <Column expander style="width: 0" />
            <Column field="id" header="ID" :sortable="true">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.plans.show',
                            params: {plan: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.id }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="name" header="Name" :sortable="true">
                <template #body="{data}">
                    <RouterLink
                        :to="{
                            name: 'admin.plans.show',
                            params: {plan: data.id},
                        }"
                        class="hover:underline"
                    >
                        {{ data.name }}
                    </RouterLink>
                </template>
            </Column>
            <Column field="slug" header="Slug" :sortable="true" />
            <Column field="price" header="Price" :sortable="true">
                <template #body="{data}">
                    {{ Intl.NumberFormat("en-GB", {style: "currency", currency: "GBP"}).format(data.price) }}
                </template>
            </Column>
            <Column
                field="is_active"
                header="Status"
                :sortable="true"
                :showFilterMatchModes="false"
                filter-field="is_active"
            >
                <template #body="{data}">
                    <span
                        :class="data.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                        class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                    >
                        {{ data.is_active ? "Active" : "Inactive" }}
                    </span>
                </template>
                <template #filter="{filterModel, filterCallback}">
                    <MultiSelect
                        v-model="filterModel.value"
                        @change="filterCallback"
                        :options="[true, false]"
                        placeholder="Select status"
                        class="p-column-filter"
                        style="min-width: 12rem"
                        :maxSelectedLabels="1"
                    >
                        <template #option="slotProps">
                            <div class="align-items-center flex">
                                <span>{{ slotProps.option ? "Active" : "Inactive" }}</span>
                            </div>
                        </template>
                        <template #value="slotProps">
                            <div v-if="slotProps.value && slotProps.value.length" class="flex items-center">
                                <span>{{ slotProps.value[0] ? "Active" : "Inactive" }}</span>
                            </div>
                            <span v-else>{{ slotProps.placeholder }}</span>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column field="created_at" header="Created At" :sortable="true">
                <template #body="{data}">
                    {{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}
                </template>
            </Column>
            <Column field="deleted_at" header="Deleted At" :sortable="true">
                <template #body="{data}">
                    {{ data.deleted_at ? $date(data.deleted_at).format("DD/MM/YYYY HH:mm") : "-" }}
                </template>
            </Column>
            <Column header="Actions" style="width: 0">
                <template #body="{data}">
                    <TableRecordActions
                        :name="data.name"
                        :routes="{
                            show: {
                                name: 'admin.plans.show',
                                params: {plan: data.id},
                            },
                            edit: {
                                name: 'admin.plans.edit',
                                params: {plan: data.id},
                            },
                            delete: route('admin.plans.destroy', data.id),
                        }"
                        @deleted="getPlans"
                    />
                </template>
            </Column>
            <template #expansion="{data}">
                <h2 class="mb-2 text-xl font-bold">{{ data.name }}</h2>
                <table>
                    <tbody>
                        <tr>
                            <td>ID</td>
                            <td>{{ data.id }}</td>
                        </tr>
                        <tr>
                            <td>Slug</td>
                            <td>{{ data.slug }}</td>
                        </tr>
                        <tr>
                            <td>Description</td>
                            <td>{{ data.description || "-" }}</td>
                        </tr>
                        <tr>
                            <td>Price</td>
                            <td>
                                {{
                                    Intl.NumberFormat("en-GB", {style: "currency", currency: "GBP"}).format(data.price)
                                }}
                            </td>
                        </tr>
                        <tr>
                            <td>Stripe Product ID</td>
                            <td>{{ data.stripe_product_id || "-" }}</td>
                        </tr>
                        <tr>
                            <td>Stripe Price ID</td>
                            <td>{{ data.stripe_price_id || "-" }}</td>
                        </tr>
                        <tr>
                            <td>Status</td>
                            <td>
                                <span
                                    :class="data.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                                    class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                >
                                    {{ data.is_active ? "Active" : "Inactive" }}
                                </span>
                            </td>
                        </tr>
                        <tr>
                            <td>Restrictions</td>
                            <td>
                                <div v-if="data.restrictions && Object.keys(data.restrictions).length > 0">
                                    <div v-for="(value, key) in data.restrictions" :key="key" class="text-sm">
                                        <strong>{{ key }}:</strong>
                                        {{ value }}
                                    </div>
                                </div>
                                <span v-else>-</span>
                            </td>
                        </tr>
                        <tr>
                            <td>Created At</td>
                            <td>{{ $date(data.created_at).format("DD/MM/YYYY HH:mm") }}</td>
                        </tr>
                        <tr>
                            <td>Updated At</td>
                            <td>{{ $date(data.updated_at).format("DD/MM/YYYY HH:mm") }}</td>
                        </tr>
                        <tr>
                            <td>Deleted At</td>
                            <td>
                                <span v-if="data.deleted_at">
                                    {{ $date(data.deleted_at).format("DD/MM/YYYY HH:mm") }}
                                </span>
                                <span v-else>-</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </DataTable>
    </div>
</template>
<style scoped>
tr td {
    @apply py-1 pr-2;
}

tr td:first-child {
    @apply font-bold;
}
</style>
